import type { Machine } from '@bridgewerk/shared';

export interface WerkOsInsightCardProps {
  readonly machine: Machine;
}

export function WerkOsInsightCard({ machine }: WerkOsInsightCardProps): JSX.Element {
  const recommendation = deriveRecommendation(machine);
  return (
    <section
      className="rounded-md border p-5"
      style={{
        borderColor: 'rgba(0,196,232,0.25)',
        background:
          'linear-gradient(180deg, rgba(0,196,232,0.06) 0%, rgba(0,196,232,0.02) 100%)',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
          ⚡ WerkOS recommendation
        </h2>
        <span className="font-mono text-[10px] text-text-dim">just now</span>
      </div>
      <p className="text-sm leading-snug text-text">{recommendation}</p>
    </section>
  );
}

function deriveRecommendation(m: Machine): string {
  if (m.status === 'alarm' && m.alarm_code) {
    return `Active alarm ${m.alarm_code}. Acknowledge on the operator panel and check the program for the last completed line.`;
  }
  if (m.status === 'offline') {
    return `Machine offline since ${new Date(m.last_seen_at).toLocaleTimeString()}. Confirm power and the dongle's network LED.`;
  }
  if (m.status === 'idle') {
    return 'Idle for the last cycle. Schedule the next job to keep utilization above 70%.';
  }
  if (m.oee_percent < 50) {
    return `OEE at ${m.oee_percent.toFixed(0)}% — well below your 70% target. Inspect feed-rate overrides and tool wear.`;
  }
  if (m.spindle_rpm > 12_000) {
    return 'Spindle speed elevated. Confirm the program matches the loaded tool to avoid premature wear.';
  }
  return 'Operating within expected envelopes. Insights compound after 7 days of telemetry.';
}
